<template>
    <!-- Modalità Compact per widget dashboard -->
    <div v-if="compact" class="compact-report-widget fill-height">
        <!-- Aggiornamento: barra sottile, i dati precedenti restano visibili -->
        <v-progress-linear v-if="loading && hasLoaded" indeterminate color="primary" height="2" absolute top></v-progress-linear>

        <!-- Primo caricamento -->
        <div v-if="loading && !hasLoaded" class="d-flex align-center justify-center fill-height">
            <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="d-flex align-center justify-center fill-height error--text text-center pa-2">
            <div>
                <v-icon small color="error">mdi-alert-circle</v-icon>
                <div class="caption mt-1">{{ error }}</div>
            </div>
        </div>

        <!-- Content -->
        <div v-else class="fill-height" :class="{ 'widget-refreshing': loading }">
            <!-- Counter Chart -->
            <div v-if="chartData && chartData.type === 'counter'" class="counter-display-compact fill-height d-flex flex-column align-center justify-center">
                <div class="text-h4 font-weight-bold counter-value">{{ formattedCounterValue }}</div>
                <div class="counter-bar primary mt-2 mb-1"></div>
                <div class="caption grey--text">{{ chartData.categoryField }}</div>
            </div>

            <!-- Tile troppo basso per un grafico leggibile: totale + sparkline -->
            <div v-else-if="chartData && chartData.type && compactMini" class="counter-display-compact fill-height d-flex flex-column align-center justify-center">
                <div class="text-h5 font-weight-bold counter-value">{{ miniTotal }}</div>
                <div class="caption grey--text">{{ miniLabel }}</div>
                <svg v-if="sparklinePoints" class="sparkline primary--text mt-1" viewBox="0 0 100 24" preserveAspectRatio="none">
                    <polyline :points="sparklinePoints" fill="none" stroke="currentColor" stroke-width="2" vector-effect="non-scaling-stroke"></polyline>
                </svg>
            </div>

            <!-- AMCharts Container -->
            <div
                v-else-if="chartData && chartData.type && chartData.type !== 'counter'"
                :id="chartContainerId"
                class="chart-container-compact">
            </div>

            <!-- Table View (fallback when no chart) -->
            <div v-else-if="results.length" class="compact-table-wrapper">
                <v-data-table
                    :headers="tableHeaders"
                    :items="filteredResults"
                    :items-per-page="10"
                    item-key="_rowIndex"
                    class="elevation-0"
                    dense>
                    <!-- Slot per gli header con campi di ricerca -->
                    <template v-for="col in tableHeaders" v-slot:[`header.${col.value}`]>
                        <div :key="col.value" class="d-flex flex-column text-left" :class="showColumnFilters ? 'mb-3' : 'mb-1'" style="position: relative;">
                            <div class="font-weight-bold">{{ col.text }}</div>
                            <v-text-field
                                v-if="showColumnFilters"
                                v-model="columnFilters[col.value]"
                                class="mt-1"
                                dense
                                hide-details
                                outlined
                                clearable
                                placeholder="Cerca..."
                                @click.stop
                                @click:clear="columnFilters[col.value] = ''"
                                style="font-size: 12px; max-width: 100%;">
                            </v-text-field>
                        </div>
                    </template>

                    <!-- Slot per renderizzare i valori nelle celle -->
                    <template v-for="col in tableHeaders" v-slot:[`item.${col.value}`]="{ item }">
                        <span :key="col.value" :class="cellClass(col)" v-html="formatCell(item[col.value], col.type, col.value)"></span>
                    </template>

                    <template v-slot:[`footer.page-text`]="{ pageStart, pageStop }">
                        Da {{ pageStart }} a {{ pageStop }} di {{ filteredResults.length }}
                    </template>
                </v-data-table>
            </div>

            <!-- No data -->
            <div v-else class="d-flex align-center justify-center fill-height">
                <div class="text-center">
                    <v-icon small color="grey">mdi-database-off</v-icon>
                    <div class="caption grey--text mt-1">Nessun dato</div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modalità Full per pagina report -->
    <v-row v-else-if="!$store.getters.progress" class="ma-0 pb-offset">
        <v-col cols="12" class="pa-1">
            <v-card class="background-2 round-card">
                <!-- Header: titolo, vista, azioni principali, menu altre azioni -->
                <v-card-title class="pa-3 flex-nowrap">
                    <v-icon left>mdi-chart-box</v-icon>
                    <span class="pl-2 text-truncate">{{ model.name }}</span>
                    <v-spacer></v-spacer>

                    <v-btn-toggle v-model="viewMode" mandatory dense group color="primary" class="mr-3">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn value="table" icon v-on="on">
                                    <v-icon>mdi-table</v-icon>
                                </v-btn>
                            </template>
                            <span>Tabella</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn value="chart" icon :disabled="!model.chart_type || !results.length" v-on="on">
                                    <v-icon>mdi-chart-arc</v-icon>
                                </v-btn>
                            </template>
                            <span>Grafico</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn value="both" icon :disabled="!model.chart_type || !results.length" v-on="on">
                                    <v-icon>mdi-view-split-horizontal</v-icon>
                                </v-btn>
                            </template>
                            <span>Grafico e tabella</span>
                        </v-tooltip>
                    </v-btn-toggle>

                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                color="primary"
                                @click="executeReport"
                                :loading="loading"
                                class="mr-2"
                                v-on="on">
                                <v-icon>mdi-refresh</v-icon>
                            </v-btn>
                        </template>
                        <span>Aggiorna report</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                :color="autoRefresh ? 'primary' : 'grey'"
                                @click="toggleAutoRefresh"
                                class="mr-2"
                                v-on="on">
                                <v-icon>{{ autoRefresh ? 'mdi-autorenew' : 'mdi-autorenew-off' }}</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ autoRefresh ? 'Disattiva auto-aggiornamento' : 'Attiva auto-aggiornamento (60s)' }}</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                color="success"
                                @click="exportCsv"
                                :disabled="!results.length"
                                class="mr-2"
                                v-on="on">
                                <v-icon>mdi-file-excel</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ hasSelectedItems ? `Esporta CSV (${selectedItemsCount} selezionati)` : 'Esporta CSV (tutti)' }}</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                color="warning"
                                @click="clearFilters"
                                :disabled="!Object.keys(columnFilters).length || !hasActiveFilters"
                                class="mr-2"
                                v-on="on">
                                <v-icon>mdi-filter-remove</v-icon>
                            </v-btn>
                        </template>
                        <span>Pulisci filtri</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="hasSelectedItems">
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                color="error"
                                @click="clearSelection"
                                class="mr-2"
                                v-on="on">
                                <v-icon>mdi-checkbox-multiple-blank-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>Deseleziona tutto ({{ selectedItemsCount }})</span>
                    </v-tooltip>

                    <button-show-generator
                        :id="model.id"
                        :info="info"
                        :model="model"
                    ></button-show-generator>
                </v-card-title>

                <!-- Aggiornamento in corso: barra sottile al posto del divisore -->
                <v-progress-linear v-if="loading && hasLoaded" indeterminate color="primary" height="2"></v-progress-linear>
                <v-divider v-else></v-divider>

                <!-- Descrizione report -->
                <v-card-text v-if="model.description" class="pa-3 pb-1">
                    <p class="mb-0 body-2 grey--text text--darken-1">{{ model.description }}</p>
                </v-card-text>

                <!-- Pannello filtri utente: chip sempre visibili, campi a scomparsa -->
                <div v-if="filterDefinitions.length" class="widget-filter-panel background-1 mx-3 mt-3 mb-2">
                    <div class="d-flex align-center flex-wrap px-3 py-2 filter-panel-header" @click="filtersExpanded = !filtersExpanded">
                        <v-icon small color="primary" class="mr-2">mdi-filter-variant</v-icon>
                        <span class="body-2 font-weight-medium mr-3">Filtri</span>
                        <v-chip
                            v-for="chip in activeFilterChips"
                            :key="chip.key"
                            x-small
                            label
                            outlined
                            color="primary"
                            class="mr-1 my-1 filter-chip">
                            {{ chip.text }}
                        </v-chip>
                        <span v-if="!activeFilterChips.length" class="caption grey--text">Nessun filtro attivo: tutti i dati</span>
                        <v-spacer></v-spacer>
                        <v-icon small>{{ filtersExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </div>
                    <v-expand-transition>
                        <div v-show="filtersExpanded" class="px-3 pb-3">
                            <widget-filters
                                v-model="filterValues"
                                :definitions="filterDefinitions"
                                @apply="executeReport"
                            ></widget-filters>
                        </div>
                    </v-expand-transition>
                </div>

                <!-- Visualizzazione risultati -->
                <v-card-text class="pa-0 pt-3 ma-0 pb-5">
                    <!-- Primo caricamento -->
                    <div v-if="loading && !hasLoaded" class="pa-5 text-center">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        <p class="mt-3 mb-0">Esecuzione report in corso...</p>
                    </div>

                    <div v-else :class="{ 'widget-refreshing': loading }">
                        <!-- Campo di ricerca globale -->
                        <v-row v-if="results.length && !error" class="mx-3 mb-3 mt-0 justify-center">
                            <v-col cols="12" md="6" lg="4">
                                <v-text-field
                                    v-model="globalSearch"
                                    prepend-inner-icon="mdi-magnify"
                                    label="Ricerca"
                                    placeholder="Cerca in tutte le colonne..."
                                    outlined
                                    dense
                                    clearable
                                    hide-details
                                    class="global-search-field"
                                    @click:clear="globalSearch = ''">
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <!-- Badge ricerche attive -->
                        <v-alert v-if="hasActiveFilters" type="info" dense text class="mx-3 mb-3" border="left">
                            <div class="d-flex align-center">
                                <v-icon left small>mdi-text-search</v-icon>
                                <span>{{ activeFiltersCount }} ricerca/e attiva/e - Visualizzazione di {{ filteredResults.length }} su {{ results.length }} risultati</span>
                            </div>
                        </v-alert>

                        <!-- Messaggio errore -->
                        <v-alert v-if="error" type="error" dense class="mx-3 mb-3">
                            {{ error }}
                        </v-alert>

                        <!-- Messaggio nessun risultato -->
                        <v-alert v-if="!error && !results.length" type="info" dense text class="mx-3 mb-3">
                            Nessun risultato{{ activeFilterChips.length ? ' con i filtri impostati' : '' }}. Clicca su aggiorna per eseguire di nuovo il report.
                        </v-alert>

                        <template v-if="results.length && !error">
                            <!-- Grafico a tutta larghezza -->
                            <div v-if="viewMode === 'chart' || viewMode === 'both'" class="mx-3 mb-3">
                                <v-card outlined class="pa-3 background-2">
                                    <div
                                        :id="chartContainerId"
                                        :style="{ height: viewMode === 'both' ? '320px' : '440px' }">
                                    </div>
                                </v-card>
                            </div>

                            <!-- Tabella sotto il grafico -->
                            <div v-if="viewMode === 'table' || viewMode === 'both'" class="mx-0 pa-0">
                                <v-divider></v-divider>
                                <v-data-table
                                    show-select
                                    v-model="selectedItems"
                                    :headers="tableHeaders"
                                    :items="filteredResults"
                                    :items-per-page="15"
                                    item-key="_rowIndex"
                                    class="elevation-0 pa-0 ma-0 mt-3 report-table"
                                    dense>
                                    <!-- Checkbox header color -->
                                    <template v-slot:header.data-table-select="{ on, props }">
                                        <v-simple-checkbox color="primary" :class="showColumnFilters ? 'mt-7' : ''" v-bind="props" v-on="on"></v-simple-checkbox>
                                    </template>

                                    <!-- Checkbox item color -->
                                    <template v-slot:item.data-table-select="{ isSelected, select }">
                                        <v-simple-checkbox color="primary" :value="isSelected" @input="select($event)"></v-simple-checkbox>
                                    </template>

                                    <!-- Slot per gli header con campi di ricerca -->
                                    <template v-for="col in tableHeaders" v-slot:[`header.${col.value}`]>
                                        <div :key="col.value" class="d-flex flex-column text-left" :class="showColumnFilters ? 'mb-3' : ''" style="position: relative;">
                                            <div class="font-weight-bold">{{ col.text }}</div>
                                            <v-text-field
                                                v-if="showColumnFilters"
                                                v-model="columnFilters[col.value]"
                                                class="mt-1"
                                                dense
                                                hide-details
                                                outlined
                                                clearable
                                                placeholder="Cerca..."
                                                @click.stop
                                                @click:clear="columnFilters[col.value] = ''"
                                                style="font-size: 12px; max-width: 100%;">
                                            </v-text-field>
                                        </div>
                                    </template>

                                    <!-- Slot per renderizzare i valori formattati nelle celle -->
                                    <template v-for="col in tableHeaders" v-slot:[`item.${col.value}`]="{ item }">
                                        <span :key="col.value" :class="cellClass(col)" v-html="formatCell(item[col.value], col.type, col.value)"></span>
                                    </template>

                                    <template v-slot:[`footer.page-text`]="{ pageStart, pageStop }">
                                        Da {{ pageStart }} a {{ pageStop }} di {{ filteredResults.length }}
                                    </template>
                                </v-data-table>
                            </div>
                        </template>
                    </div>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>
<script>
    import * as am4core from '@amcharts/amcharts4/core'
    import * as am4charts from '@amcharts/amcharts4/charts'
    import am4themes_animated from '@amcharts/amcharts4/themes/animated'
    import am4themes_material from '@amcharts/amcharts4/themes/material'
    import am4lang_it_IT from '@amcharts/amcharts4/lang/it_IT'
    import ButtonShowGenerator from '@/components/common/ButtonShowGenerator'
    import WidgetFilters from './WidgetFilters.vue'
    import { widgetFilterDefaults, widgetFilterChips } from './widgetFilterDefaults'
    import { currencySymbol, detectColumnTypes, formatCell, formatNumber, isTotalColumn, plainCell } from './widgetFormat'

    am4core.useTheme(am4themes_material)
    am4core.useTheme(am4themes_animated)

export default {
    name: 'widget-view',
    components: {
        ButtonShowGenerator,
        WidgetFilters
    },
    props: {
        // Per uso come widget nella dashboard
        reportId: { type: [Number, String], default: null },
        compact: { type: Boolean, default: false }
    },
    data() {
        return {
            model: {},
            info: {},
            results: [],
            columns: [],
            chartData: null,
            chart: null,
            loading: false,
            error: null,
            viewMode: 'table',
            autoRefresh: false,
            refreshInterval: null,
            columnFilters: {},
            globalSearch: '',
            selectedItems: [],
            // Filtri utente dichiarati sul widget e valori correnti inviati al backend
            filterDefinitions: [],
            filterValues: {},
            filtersExpanded: true,
            // true dopo la prima risposta: gli aggiornamenti successivi non svuotano la vista
            hasLoaded: false,
            // Campi di ricerca nelle intestazioni della tabella (sempre visibili)
            showColumnFilters: true,
            // Tipo dedotto per ogni colonna: number, date, datetime, html, text
            columnTypes: {},
            // Tile dashboard troppo basso per un grafico: mostra totale + sparkline
            compactMini: false,
            // Ordinamento per tabella compact
            compactSortBy: null,
            compactSortDesc: false
        }
    },

    computed: {
        // ID del report corrente (da prop o da route)
        currentReportId() {
            return this.reportId || this.$route?.params?.id
        },

        // ID univoco per il container del grafico
        chartContainerId() {
            return 'chart-' + this.currentReportId + (this.compact ? '-compact' : '')
        },

        // Valuta delle colonne numeriche dichiarata dal widget (chart_config.currency, es. 'EUR')
        currency() {
            return this.model.chart_config?.currency || null
        },

        // Valore formattato per counter in modalità compact
        formattedCounterValue() {
            if (!this.chartData?.data?.[0]) return '-'
            const value = this.chartData.data[0][this.chartData.valueField]
            if (typeof value === 'number' || !isNaN(value)) {
                return this.formatCounter(value)
            }
            return value
        },

        tableHeaders() {
            if (!this.columns.length) return []

            return this.columns.map(col => {
                const type = this.columnTypes[col] || 'text'
                return {
                    text: col,
                    value: col,
                    type,
                    emphasis: isTotalColumn(col),
                    sortable: true,
                    align: type === 'number' ? 'end' : 'start',
                    class: type === 'number' ? 'text-right' : 'text-left',
                    filter: value => {
                        if (!this.columnFilters[col]) return true
                        return String(value).toLowerCase().includes(this.columnFilters[col].toLowerCase())
                    }
                }
            })
        },

        // Chip dei filtri utente attivi mostrate nel pannello
        activeFilterChips() {
            return widgetFilterChips(this.filterDefinitions, this.filterValues)
        },

        // Palette dei grafici dal tema Vuetify, coerente con il resto dell'app e con la dark mode
        chartPalette() {
            const theme = this.$vuetify.theme.themes[this.$vuetify.theme.dark ? 'dark' : 'light'] || {}
            const pick = name => {
                const c = theme[name]
                return c && typeof c === 'object' ? c.base : c
            }
            return ['primary', 'info', 'success', 'warning', 'error', 'secondary', 'accent']
                .map(pick)
                .filter(Boolean)
                .concat(['#9C27B0', '#00BCD4', '#795548', '#607D8B'])
        },

        // Serie di valori usata per totale e sparkline del tile compatto
        miniValues() {
            if (!this.chartData || !this.chartData.data) return []
            const field = this.chartData.valueField || (this.chartData.valueFields || [])[0]
            if (!field) return []
            return this.chartData.data.map(r => parseFloat(r[field])).filter(v => !isNaN(v))
        },

        miniTotal() {
            return formatNumber(this.miniValues.reduce((a, b) => a + b, 0), this.currency)
        },

        miniLabel() {
            if (!this.chartData) return ''
            const field = this.chartData.valueField || (this.chartData.valueFields || [])[0] || ''
            return `${field} · ${this.chartData.data.length} ${this.chartData.categoryField}`
        },

        sparklinePoints() {
            const values = this.miniValues
            if (values.length < 2) return ''
            const max = Math.max(...values)
            const min = Math.min(...values)
            const span = max - min || 1
            return values.map((v, i) => {
                const x = (i / (values.length - 1)) * 100
                const y = 22 - ((v - min) / span) * 20
                return `${x.toFixed(1)},${y.toFixed(1)}`
            }).join(' ')
        },

        filteredResults() {
            let filtered = this.results

            // Filtro globale
            if (this.globalSearch && this.globalSearch.length > 0) {
                const searchLower = this.globalSearch.toLowerCase()
                filtered = filtered.filter(item => {
                    return Object.values(item).some(value => {
                        return String(value).toLowerCase().includes(searchLower)
                    })
                })
            }

            // Filtri per colonna
            if (Object.keys(this.columnFilters).length) {
                filtered = filtered.filter(item => {
                    return Object.keys(this.columnFilters).every(col => {
                        if (!this.columnFilters[col]) return true
                        const value = item[col]
                        return String(value).toLowerCase().includes(this.columnFilters[col].toLowerCase())
                    })
                })
            }

            return filtered
        },

        // Risultati filtrati e ordinati per tabella compact
        sortedFilteredResults() {
            let results = [...this.filteredResults]

            if (this.compactSortBy) {
                results.sort((a, b) => {
                    let aVal = a[this.compactSortBy]
                    let bVal = b[this.compactSortBy]

                    // Gestione valori null/undefined
                    if (aVal == null) aVal = ''
                    if (bVal == null) bVal = ''

                    // Prova a confrontare come numeri
                    const aNum = parseFloat(String(aVal).replace(/[^\d.-]/g, ''))
                    const bNum = parseFloat(String(bVal).replace(/[^\d.-]/g, ''))

                    if (!isNaN(aNum) && !isNaN(bNum)) {
                        return this.compactSortDesc ? bNum - aNum : aNum - bNum
                    }

                    // Confronto come stringhe
                    const comparison = String(aVal).localeCompare(String(bVal), 'it', { numeric: true })
                    return this.compactSortDesc ? -comparison : comparison
                })
            }

            return results
        },

        hasActiveFilters() {
            return Object.values(this.columnFilters).some(filter => filter && filter.length > 0) || (this.globalSearch && this.globalSearch.length > 0)
        },

        activeFiltersCount() {
            let count = Object.values(this.columnFilters).filter(f => f && f.length > 0).length
            if (this.globalSearch && this.globalSearch.length > 0) count++
            return count
        },

        hasSelectedItems() {
            return this.selectedItems.length > 0
        },

        selectedItemsCount() {
            return this.selectedItems.length
        }
    },

    methods: {
        async loadReport() {
            if (!this.currentReportId) return

            try {
                if (!this.compact) {
                    this.$store.commit('startProgress')
                }
                const response = await this.axios.get(`widgets/${this.currentReportId}`)

                if (response.data && response.data.model) {
                    this.model = response.data.model
                    this.info = response.data.info
                    this.filterDefinitions = response.data.filter_definitions || []
                    this.filterValues = widgetFilterDefaults(this.filterDefinitions)
                    this.$emit('filters-loaded', { definitions: this.filterDefinitions, values: this.filterValues })
                    await this.executeReport()
                }

                if (!this.compact) {
                    this.$store.commit('endProgress')
                }
            } catch (error) {
                if (!this.compact) {
                    this.$store.commit('endProgress')
                    this.$store.commit('setSnackbar', {
                        text: 'Errore nel caricamento del report',
                        color: 'error',
                        value: true
                    })
                } else {
                    this.error = 'Errore caricamento'
                }
            }
        },

        async executeReport() {
            if (!this.currentReportId) return

            try {
                this.loading = true
                this.error = null

                const response = await this.axios.post(`widgets-execute/${this.currentReportId}`, { filters: this.filterValues })

                if (response.data && response.data.status === 'success') {
                    this.results = (response.data.results || []).map((item, index) => ({
                        ...item,
                        _rowIndex: index
                    }))
                    this.columns = response.data.columns || []
                    this.columnTypes = detectColumnTypes(this.columns, this.results)
                    this.chartData = response.data.chart || null
                    this.selectedItems = []
                    this.hasLoaded = true

                    // Crea il grafico se presente
                    // In modalità compact mostra sempre il grafico se disponibile
                    const shouldShowChart = this.compact
                        ? (this.chartData && this.chartData.type && this.chartData.type !== 'counter')
                        : (this.chartData && this.chartData.type && (this.viewMode === 'chart' || this.viewMode === 'both'))

                    // Setto loading = false PRIMA di creare il grafico per permettere al DOM di renderizzare il container
                    this.loading = false

                    if (shouldShowChart) {
                        this.$nextTick(() => {
                            this.createChart()
                        })
                    }
                } else {
                    this.error = response.data.message || 'Errore nell\'esecuzione del report'
                    this.loading = false
                    this.hasLoaded = true
                }
            } catch (error) {
                this.loading = false
                this.hasLoaded = true
                this.error = error.response?.data?.message || 'Errore nell\'esecuzione del report'
            }
        },

        // Formattazione celle (numeri, date, HTML) e stile delle colonne totali.
        // Con chart_config.currency_columns la valuta vale solo per le colonne elencate
        formatCell(value, type, col) {
            const columns = this.model.chart_config?.currency_columns
            return formatCell(value, type, !columns || columns.includes(col) ? this.currency : null)
        },

        // Numero del contatore: con la valuta se il widget la dichiara
        formatCounter(value) {
            return this.currency ? formatNumber(value, this.currency) : new Intl.NumberFormat('it-IT').format(value)
        },

        cellClass(col) {
            return {
                'font-weight-bold': col.emphasis,
                'text-no-wrap': col.type === 'number' || col.type === 'date' || col.type === 'datetime'
            }
        },

        // Colori, locale e formato numeri condivisi da tutti i grafici
        applyChartTheme(chart) {
            chart.colors.list = this.chartPalette.map(c => am4core.color(c))
            chart.language.locale = am4lang_it_IT
            chart.numberFormatter.numberFormat = this.currency
                ? "#,###.00' " + currencySymbol(this.currency) + "'"
                : '#,###.##'
            chart.fontSize = this.compact ? 10 : 12
            chart.paddingRight = this.compact ? 8 : 16
            if (this.compact) {
                chart.paddingTop = 4
                chart.paddingBottom = 4
                chart.paddingLeft = 4
            }
        },

        // Legenda: in basso su una riga scorrevole nel tile, a destra scorrevole nella pagina (torta)
        configureLegend(legend, textColor, { pieFull } = {}) {
            legend.labels.template.fill = textColor
            legend.valueLabels.template.fill = textColor
            legend.useDefaultMarker = true
            legend.markers.template.width = this.compact ? 10 : 14
            legend.markers.template.height = this.compact ? 10 : 14
            legend.itemContainers.template.paddingTop = this.compact ? 1 : 4
            legend.itemContainers.template.paddingBottom = this.compact ? 1 : 4
            legend.scrollable = true
            if (this.compact) {
                legend.position = 'bottom'
                legend.maxHeight = 36
                legend.labels.template.truncate = true
                legend.labels.template.maxWidth = 90
            } else if (pieFull) {
                legend.position = 'right'
                legend.maxWidth = 260
                legend.maxHeight = 380
                legend.labels.template.truncate = true
                legend.labels.template.maxWidth = 170
            } else {
                legend.position = 'bottom'
            }
        },

        // Asse categorie: etichette troncate, nascoste nel tile quando le categorie sono troppe
        configureCategoryAxis(axis, textColor, gridColor) {
            axis.renderer.grid.template.location = 0
            axis.renderer.labels.template.fill = textColor
            axis.renderer.grid.template.stroke = gridColor
            axis.renderer.labels.template.truncate = true
            axis.renderer.labels.template.maxWidth = this.compact ? 70 : 140
            axis.renderer.minGridDistance = this.compact ? 30 : 40
            const categories = this.chartData.data.length
            if (this.compact && categories > 12) {
                axis.renderer.labels.template.disabled = true
                axis.renderer.grid.template.disabled = true
            }
        },

        createChart() {
            // Dispone del vecchio grafico se esiste
            if (this.chart) {
                this.chart.dispose()
                this.chart = null
            }

            if (!this.chartData || !this.chartData.data || !this.chartData.data.length) {
                return
            }

            // Nel tile: un grafico complesso (molte serie o categorie) in poco spazio
            // e' illeggibile, meglio totale + sparkline
            if (this.compact) {
                const height = this.$el ? this.$el.clientHeight : 0
                const seriesCount = (this.chartData.valueFields || [this.chartData.valueField]).length
                const complex = seriesCount > 2 || this.chartData.data.length > 12
                const mini = this.chartData.type !== 'counter' && height > 0 && (height < 110 || (height < 170 && complex))
                if (mini !== this.compactMini) {
                    this.compactMini = mini
                    if (!mini) {
                        // il container del grafico compare al prossimo render
                        this.$nextTick(() => this.buildChart())
                    }
                    return
                }
                if (mini) return
            }

            this.buildChart()
        },

        buildChart() {
            const chartId = this.chartContainerId
            if (!document.getElementById(chartId)) return

            switch (this.chartData.type) {
                case 'counter':
                    this.createCounterChart(chartId)
                    break
                case 'pie':
                    this.createPieChart(chartId)
                    break
                case 'bar':
                    this.createBarChart(chartId)
                    break
                case 'line':
                    this.createLineChart(chartId)
                    break
                case 'column':
                    this.createColumnChart(chartId)
                    break
            }
        },

        createCounterChart(chartId) {
            const chartElement = document.getElementById(chartId)
            if (!chartElement) return

            const firstRow = this.chartData.data[0]
            const value = firstRow[this.chartData.valueField] || 0
            const label = this.chartData.categoryField || 'Valore'

            // Formatta il valore se è un numero
            let displayValue = value
            if (typeof value === 'number' || !isNaN(value)) {
                displayValue = this.formatCounter(value)
            }

            // Detect dark mode
            const isDark = this.$vuetify.theme.dark

            const background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'
            const valueColor = isDark ? '#FFFFFF' : '#212121'
            const labelColor = isDark ? '#B0BEC5' : '#616161'
            const accent = this.chartPalette[0] || '#FFC107'

            chartElement.innerHTML = `
                <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; height: 100%; background: ${background}; border-radius: 8px; padding: 24px;">
                    <div style="font-size: 72px; font-weight: 700; color: ${valueColor}; line-height: 1;">
                        ${displayValue}
                    </div>
                    <div style="width: 48px; height: 4px; border-radius: 2px; background: ${accent}; margin: 16px 0 12px;"></div>
                    <div style="font-size: 18px; color: ${labelColor}; font-weight: 500;">
                        ${label}
                    </div>
                </div>
            `
        },

        createPieChart(chartId) {
            this.chart = am4core.create(chartId, am4charts.PieChart)
            this.chart.data = this.chartData.data
            this.chart.innerRadius = am4core.percent(50)
            this.applyChartTheme(this.chart)

            // Dark mode colors
            const isDark = this.$vuetify.theme.dark
            const textColor = isDark ? am4core.color("#B0BEC5") : am4core.color("#424242")

            let pieSeries = this.chart.series.push(new am4charts.PieSeries())
            pieSeries.dataFields.value = this.chartData.valueField
            pieSeries.dataFields.category = this.chartData.categoryField
            pieSeries.slices.template.tooltipText = "[font-size: 12px]{category}: {value.percent}% ({value})[/]"

            pieSeries.hiddenState.properties.opacity = 1
            pieSeries.hiddenState.properties.endAngle = -90
            pieSeries.hiddenState.properties.startAngle = -90

            pieSeries.labels.template.text = "[font-size: 11px]{category}: {value.percent.formatNumber('#.#')} %[/]"
            pieSeries.labels.template.fill = textColor
            pieSeries.labels.template.truncate = true
            pieSeries.labels.template.maxWidth = this.compact ? 80 : 160

            // Troppe fette: niente etichette intorno alla torta, resta la legenda scorrevole
            const slices = this.chartData.data.length
            if ((this.compact && slices > 6) || slices > 15) {
                pieSeries.labels.template.disabled = true
                pieSeries.ticks.template.disabled = true
            }

            this.chart.legend = new am4charts.Legend()
            this.configureLegend(this.chart.legend, textColor, { pieFull: true })
            this.chart.legend.labels.template.text = "{category}"
            pieSeries.legendSettings.valueText = "{value.percent.formatNumber('#.#')} %"
        },

        createBarChart(chartId) {
            this.chart = am4core.create(chartId, am4charts.XYChart)
            this.chart.data = this.chartData.data
            this.applyChartTheme(this.chart)

            // Dark mode colors
            const isDark = this.$vuetify.theme.dark
            const textColor = isDark ? am4core.color("#B0BEC5") : am4core.color("#424242")
            const gridColor = isDark ? am4core.color("#444") : am4core.color("#e0e0e0")

            let categoryAxis = this.chart.yAxes.push(new am4charts.CategoryAxis())
            categoryAxis.dataFields.category = this.chartData.categoryField
            this.configureCategoryAxis(categoryAxis, textColor, gridColor)

            let valueAxis = this.chart.xAxes.push(new am4charts.ValueAxis())
            valueAxis.renderer.minWidth = 50
            valueAxis.renderer.labels.template.fill = textColor
            valueAxis.renderer.grid.template.stroke = gridColor

            // Controlla se è multi-serie
            if (this.chartData.multiSeries && this.chartData.valueFields) {
                // Crea una serie per ogni campo valore
                this.chartData.valueFields.forEach((valueField, index) => {
                    let series = this.chart.series.push(new am4charts.ColumnSeries())
                    series.dataFields.valueX = valueField
                    series.dataFields.categoryY = this.chartData.categoryField
                    series.name = valueField
                    series.columns.template.tooltipText = "{name}: [bold]{valueX}[/]"
                    series.columns.template.strokeWidth = 0
                    series.clustered = true
                })

                // Aggiungi legenda
                this.chart.legend = new am4charts.Legend()
                this.configureLegend(this.chart.legend, textColor)
            } else {
                // Serie singola
                let series = this.chart.series.push(new am4charts.ColumnSeries())
                series.dataFields.valueX = this.chartData.valueField
                series.dataFields.categoryY = this.chartData.categoryField
                series.columns.template.tooltipText = "{categoryY}: [bold]{valueX}[/]"
                series.columns.template.strokeWidth = 0
            }

            this.chart.cursor = new am4charts.XYCursor()
        },

        createLineChart(chartId) {
            this.chart = am4core.create(chartId, am4charts.XYChart)
            this.chart.data = this.chartData.data
            this.applyChartTheme(this.chart)

            // Dark mode colors
            const isDark = this.$vuetify.theme.dark
            const textColor = isDark ? am4core.color("#B0BEC5") : am4core.color("#424242")
            const gridColor = isDark ? am4core.color("#444") : am4core.color("#e0e0e0")

            let categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis())
            categoryAxis.dataFields.category = this.chartData.categoryField
            this.configureCategoryAxis(categoryAxis, textColor, gridColor)

            let valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis())
            valueAxis.renderer.labels.template.fill = textColor
            valueAxis.renderer.grid.template.stroke = gridColor

            // Controlla se è multi-serie
            if (this.chartData.multiSeries && this.chartData.valueFields) {
                // Crea una serie per ogni campo valore
                this.chartData.valueFields.forEach((valueField, index) => {
                    let series = this.chart.series.push(new am4charts.LineSeries())
                    series.dataFields.valueY = valueField
                    series.dataFields.categoryX = this.chartData.categoryField
                    series.name = valueField
                    series.strokeWidth = 2
                    series.tooltipText = "{name}: [bold]{valueY}[/]"

                    // Aggiungi bullets per i punti
                    let bullet = series.bullets.push(new am4charts.CircleBullet())
                    bullet.circle.radius = this.compact ? 2 : 4
                    bullet.circle.strokeWidth = this.compact ? 1 : 2
                })

                // Aggiungi legenda
                this.chart.legend = new am4charts.Legend()
                this.configureLegend(this.chart.legend, textColor)
            } else {
                // Serie singola
                let series = this.chart.series.push(new am4charts.LineSeries())
                series.dataFields.valueY = this.chartData.valueField
                series.dataFields.categoryX = this.chartData.categoryField
                series.strokeWidth = 2
                series.tooltipText = "{categoryX}: [bold]{valueY}[/]"
            }

            this.chart.cursor = new am4charts.XYCursor()
        },

        createColumnChart(chartId) {
            this.chart = am4core.create(chartId, am4charts.XYChart)
            this.chart.data = this.chartData.data
            this.applyChartTheme(this.chart)

            // Dark mode colors
            const isDark = this.$vuetify.theme.dark
            const textColor = isDark ? am4core.color("#B0BEC5") : am4core.color("#424242")
            const gridColor = isDark ? am4core.color("#444") : am4core.color("#e0e0e0")

            let categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis())
            categoryAxis.dataFields.category = this.chartData.categoryField
            this.configureCategoryAxis(categoryAxis, textColor, gridColor)

            let valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis())
            valueAxis.renderer.labels.template.fill = textColor
            valueAxis.renderer.grid.template.stroke = gridColor

            // Controlla se è multi-serie
            if (this.chartData.multiSeries && this.chartData.valueFields) {
                // Crea una serie per ogni campo valore
                this.chartData.valueFields.forEach((valueField, index) => {
                    let series = this.chart.series.push(new am4charts.ColumnSeries())
                    series.dataFields.valueY = valueField
                    series.dataFields.categoryX = this.chartData.categoryField
                    series.name = valueField
                    series.columns.template.tooltipText = "{name}: [bold]{valueY}[/]"
                    series.columns.template.strokeWidth = 0
                    series.clustered = true
                })

                // Aggiungi legenda
                this.chart.legend = new am4charts.Legend()
                this.configureLegend(this.chart.legend, textColor)
            } else {
                // Serie singola
                let series = this.chart.series.push(new am4charts.ColumnSeries())
                series.dataFields.valueY = this.chartData.valueField
                series.dataFields.categoryX = this.chartData.categoryField
                series.columns.template.tooltipText = "{categoryX}: [bold]{valueY}[/]"
                series.columns.template.strokeWidth = 0
            }

            this.chart.cursor = new am4charts.XYCursor()
        },

        exportCsv() {
            if (!this.results.length) return

            // Determina quali righe esportare: selezionate o tutte
            const rowsToExport = this.hasSelectedItems ? this.selectedItems : this.filteredResults

            if (!rowsToExport.length) {
                this.$store.commit('setSnackbar', {
                    text: 'Nessuna riga da esportare',
                    color: 'warning',
                    value: true
                })
                return
            }

            // Crea header CSV
            const csv = []
            csv.push(this.columns.join(';'))

            // Aggiungi righe
            rowsToExport.forEach(row => {
                const values = this.columns.map(col => {
                    let value = plainCell(row[col], this.columnTypes[col])

                    // Escape virgolette e aggiungi virgolette se contiene il separatore
                    if (typeof value === 'string' && (value.includes(';') || value.includes('"'))) {
                        return '"' + value.replace(/"/g, '""') + '"'
                    }

                    return value || ''
                })
                csv.push(values.join(';'))
            })

            // Download
            const csvContent = csv.join('\n')
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)

            link.setAttribute('href', url)
            link.setAttribute('download', `report_${this.model.name}_${Date.now()}.csv`)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            // Notifica successo
            this.$store.commit('setSnackbar', {
                text: `${rowsToExport.length} riga/righe esportate con successo`,
                color: 'success',
                value: true
            })
        },

        toggleAutoRefresh() {
            this.autoRefresh = !this.autoRefresh

            if (this.autoRefresh) {
                this.startAutoRefresh()
                this.$store.commit('setSnackbar', {
                    text: 'Auto-aggiornamento attivato (ogni 60 secondi)',
                    color: 'success',
                    value: true
                })
            } else {
                this.stopAutoRefresh()
                this.$store.commit('setSnackbar', {
                    text: 'Auto-aggiornamento disattivato',
                    color: 'info',
                    value: true
                })
            }
        },

        startAutoRefresh() {
            this.stopAutoRefresh()
            this.refreshInterval = setInterval(() => {
                this.executeReport()
            }, 60000)
        },

        stopAutoRefresh() {
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval)
                this.refreshInterval = null
            }
        },

        // Applica nuovi valori dei filtri utente (usato dal tile della dashboard)
        applyFilters(values) {
            this.filterValues = { ...values }
            return this.executeReport()
        },

        resetFilters() {
            return this.applyFilters(widgetFilterDefaults(this.filterDefinitions))
        },

        clearFilters() {
            this.columnFilters = {}
            this.globalSearch = ''
            this.$store.commit('setSnackbar', {
                text: 'Filtri rimossi',
                color: 'info',
                value: true
            })
        },

        clearSelection() {
            this.selectedItems = []
            this.$store.commit('setSnackbar', {
                text: 'Selezione rimossa',
                color: 'info',
                value: true
            })
        },

        // Formatta valori per la modalità compact
        formatCompactValue(value) {
            if (value === null || value === undefined) return '-'
            if (typeof value === 'number') {
                return new Intl.NumberFormat('it-IT').format(value)
            }
            return String(value).substring(0, 50)
        },

        // Gestisce l'ordinamento delle colonne nella tabella compact
        toggleCompactSort(column) {
            if (this.compactSortBy === column) {
                if (this.compactSortDesc) {
                    // Reset ordinamento
                    this.compactSortBy = null
                    this.compactSortDesc = false
                } else {
                    // Cambia a decrescente
                    this.compactSortDesc = true
                }
            } else {
                // Nuova colonna, ordine crescente
                this.compactSortBy = column
                this.compactSortDesc = false
            }
        }
    },

    watch: {
        viewMode(newVal) {
            if ((newVal === 'chart' || newVal === 'both') && this.chartData && this.results.length) {
                this.$nextTick(() => {
                    this.createChart()
                })
            }
        }
    },

    created() {
        this.loadReport()
    },

    beforeDestroy() {
        this.stopAutoRefresh()
        if (this.chart) {
            this.chart.dispose()
        }
    }
}
</script>

<style scoped>
.pb-offset {
    padding-bottom: 80px;
}

/* Aggiornamento non distruttivo: i dati restano visibili, attenuati */
.widget-refreshing {
    opacity: 0.55;
    pointer-events: none;
    transition: opacity 0.2s ease;
}

/* Pannello filtri utente */
.widget-filter-panel {
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
}

.theme--dark .widget-filter-panel {
    border-color: rgba(255, 255, 255, 0.1);
}

.filter-panel-header {
    cursor: pointer;
    user-select: none;
}

.filter-chip {
    max-width: 320px;
}

.filter-chip >>> .v-chip__content {
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Counter e sparkline del tile */
.counter-value {
    line-height: 1.1;
}

.counter-bar {
    width: 36px;
    height: 3px;
    border-radius: 2px;
}

.sparkline {
    width: 70%;
    max-width: 180px;
    height: 24px;
}

.global-search-field >>> .v-input__prepend-inner {
    margin-top: 8px !important;
}

/* Stili per la freccia di ordinamento - solo nella tabella */
.report-table >>> thead > tr > th {
    vertical-align: top !important;
    position: relative !important;
}

.report-table >>> thead > tr > th .v-data-table-header__icon {
    position: absolute !important;
    top: 4px !important;
    right: 8px !important;
    margin-left: 0 !important;
    opacity: 0.6;
}

.report-table >>> thead > tr > th .v-data-table-header__icon:hover {
    opacity: 1;
}

/* Stili per modalità compact (widget dashboard) */
.compact-report-widget {
    width: 100%;
    height: 100%;
    position: relative;
}

.chart-container-compact {
    width: 100%;
    height: calc(100% - 10px);
    min-height: 150px;
}

.counter-display-compact {
    padding: 12px;
}

.compact-table-wrapper {
    padding: 4px;
}

/* Dark mode styles for compact table in dashboard */
.compact-table-wrapper >>> .v-data-table {
    background: transparent !important;
}

.theme--dark .compact-table-wrapper >>> .v-data-table {
    background: #1E1E1E !important;
}

/* Header row styling */
.compact-table-wrapper >>> thead tr th {
    background: rgba(0, 0, 0, 0.04) !important;
    border-bottom: 2px solid rgba(0, 0, 0, 0.12) !important;
    vertical-align: top !important;
    padding-top: 8px !important;
    padding-bottom: 12px !important;
    position: relative !important;
}

.theme--dark .compact-table-wrapper >>> thead tr th {
    background: rgba(255, 255, 255, 0.08) !important;
    border-bottom: 2px solid rgba(255, 255, 255, 0.15) !important;
    color: #E0E0E0 !important;
}

/* Header text styling */
.compact-table-wrapper >>> thead tr th .font-weight-bold {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.theme--dark .compact-table-wrapper >>> thead tr th .font-weight-bold {
    color: #90CAF9 !important;
}

/* Filter input fields in header */
.compact-table-wrapper >>> thead tr th .v-text-field {
    margin-top: 4px;
}

.compact-table-wrapper >>> thead tr th .v-text-field .v-input__slot {
    min-height: 28px !important;
    padding: 0 8px !important;
}

.theme--dark .compact-table-wrapper >>> thead tr th .v-text-field .v-input__slot {
    background: rgba(255, 255, 255, 0.05) !important;
    border-color: rgba(255, 255, 255, 0.25) !important;
}

.theme--dark .compact-table-wrapper >>> thead tr th .v-text-field input {
    color: #FFFFFF !important;
}

.theme--dark .compact-table-wrapper >>> thead tr th .v-text-field input::placeholder {
    color: rgba(255, 255, 255, 0.4) !important;
}

/* Data rows styling */
.compact-table-wrapper >>> tbody tr td {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
    font-size: 12px !important;
    padding: 8px 16px !important;
}

.theme--dark .compact-table-wrapper >>> tbody tr td {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
    color: #E0E0E0 !important;
}

/* Alternating row colors */
.compact-table-wrapper >>> tbody tr:nth-child(even) {
    background: rgba(0, 0, 0, 0.02);
}

.theme--dark .compact-table-wrapper >>> tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.03);
}

/* Hover effect on rows */
.compact-table-wrapper >>> tbody tr:hover {
    background: rgba(25, 118, 210, 0.08) !important;
}

.theme--dark .compact-table-wrapper >>> tbody tr:hover {
    background: rgba(25, 118, 210, 0.15) !important;
}

/* Footer/pagination styling */
.compact-table-wrapper >>> .v-data-footer {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    font-size: 12px;
    padding: 4px 8px;
}

.theme--dark .compact-table-wrapper >>> .v-data-footer {
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.03);
    color: #B0BEC5;
}

.theme--dark .compact-table-wrapper >>> .v-data-footer .v-select__selection {
    color: #E0E0E0 !important;
}

/* Sort icon styling - position at top right of column header */
.compact-table-wrapper >>> thead tr th .v-data-table-header__icon {
    position: absolute !important;
    top: 10px !important;
    right: 8px !important;
    opacity: 0.6;
    font-size: 14px !important;
}

.compact-table-wrapper >>> thead tr th:hover .v-data-table-header__icon {
    opacity: 1;
}

.theme--dark .compact-table-wrapper >>> thead tr th .v-data-table-header__icon {
    color: #90CAF9 !important;
}

/* Ensure header content doesn't overlap with sort icon */
.compact-table-wrapper >>> thead tr th .d-flex {
    padding-right: 20px;
}

</style>
